import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { structureIdeaFull, expandIdeaOnWater, canExpandIdea, summarizeReflection } from '@/lib/ai-service';
import { getTemplate, normalizeTemplateType, getSchemaForTemplate } from '@/lib/templates';
import type { Idea, IdeaUpdate, GrowthStage, Mood } from '@/lib/types';
import { useToast } from '@/hooks/use-toast';

// Re-export time utilities from the shared module
export { calculateDaysSinceWatered, getProgressDots, getTimeCue } from '@/lib/time-utils';

export function useIdeas() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [ideas, setIdeas] = useState<Idea[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchIdeas = async () => {
    if (!user) {
      setIdeas([]);
      setLoading(false);
      return;
    }

    const { data, error } = await supabase
      .from('ideas')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (error) {
      toast({
        title: 'Error fetching ideas',
        description: error.message,
        variant: 'destructive',
      });
    } else {
      setIdeas((data as unknown as Idea[]) || []);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchIdeas();
  }, [user]);

  const plantSeed = async (rawInput: string, templateType: string = 'blank'): Promise<Idea | null> => {
    if (!user) return null;

    try {
      // First, create the idea with just raw input
      const { data: newIdea, error: insertError } = await supabase
        .from('ideas')
        .insert({
          user_id: user.id,
          raw_input: rawInput,
          template_type: templateType,
        } as any)
        .select()
        .single();

      if (insertError) throw insertError;

      // Add to local state immediately
      const ideaData = newIdea as unknown as Idea;
      setIdeas(prev => [ideaData, ...prev]);

      // Structure the idea with AI - INITIAL only (Title + One-liner + Problem)
      try {
        const structured = await structureIdeaFull(rawInput);
        
        const { data: updatedIdea, error: updateError } = await supabase
          .from('ideas')
          .update({
            title: structured.title,
            one_liner: structured.one_liner,
            problem: structured.problem,
            audience: structured.audience,
            proposed_solution: structured.proposed_solution,
            differentiator: structured.differentiator,
            keywords_tags: structured.keywords_tags,
            business_name_options: structured.business_name_options,
            tagline_options: structured.tagline_options,
            business_models: structured.business_models,
            mvp_outline: structured.mvp_outline,
            first_experiments: structured.first_experiments,
            risks_unknowns: structured.risks_unknowns,
            domain_suggestions: structured.domain_suggestions,
          })
          .eq('id', newIdea.id)
          .select()
          .single();

        if (updateError) throw updateError;

        // Update local state with structured data
        let updatedData = updatedIdea as unknown as Idea;
        setIdeas(prev => prev.map(i => i.id === updatedData.id ? updatedData : i));

        // Auto-generate template structure in the background
        const normType = normalizeTemplateType(templateType);
        const schema = getSchemaForTemplate(normType);
        const keys = schema.map(f => f.key);

        try {
          const structRes = await supabase.functions.invoke('generate-structure', {
            body: {
              template_type: normType,
              title: structured.title || '',
              brain_dump: rawInput,
              keys,
            },
          });

          if (!structRes.error && structRes.data?.structure) {
            const { data: structUpdated, error: structErr } = await supabase
              .from('ideas')
              .update({ structure: structRes.data.structure })
              .eq('id', newIdea.id)
              .select()
              .single();

            if (!structErr && structUpdated) {
              updatedData = structUpdated as unknown as Idea;
              setIdeas(prev => prev.map(i => i.id === updatedData.id ? updatedData : i));
            }
          }
        } catch (structError) {
          console.error('Auto structure generation failed:', structError);
          // Non-blocking — idea is still planted
        }

        toast({
          title: 'Seed planted',
          description: 'Structure generated automatically.',
        });
        
        return updatedData;
      } catch (aiError) {
        // AI failed, but idea was still created
        console.error('AI structuring failed:', aiError);
        toast({
          title: 'Idea planted',
          description: 'AI structuring is unavailable. You can edit details manually.',
        });
        return ideaData;
      }
    } catch (error) {
      toast({
        title: 'Failed to plant seed',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
      return null;
    }
  };

  const waterIdea = async (
    ideaId: string, 
    reflection: string, 
    nextStep?: string, 
    mood?: Mood
  ): Promise<boolean> => {
    if (!user) return false;

    try {
      // Get current idea to check timing
      const idea = ideas.find(i => i.id === ideaId);
      if (!idea) throw new Error('Idea not found');

      // Generate AI summary of the reflection (fire-and-forget style but we'll use it)
      const aiSummary = await summarizeReflection(reflection, idea.title, idea.one_liner);

      // Create the update record with AI summary
      const { data: insertedUpdate, error: updateError } = await supabase
        .from('idea_updates')
        .insert({
          idea_id: ideaId,
          user_id: user.id,
          reflection,
          next_step: nextStep,
          mood,
          ai_summary: aiSummary,
        })
        .select()
        .single();

      if (updateError) throw updateError;

      // Check if 24 hours have passed since last watering
      const lastWateredDate = idea.last_watered_at 
        ? new Date(idea.last_watered_at) 
        : new Date(idea.created_at);
      const hoursSinceLastWatered = (Date.now() - lastWateredDate.getTime()) / (1000 * 60 * 60);
      const canGrow = hoursSinceLastWatered >= 24;

      const updatePayload: Partial<Idea> = {
        last_watered_at: new Date().toISOString(),
        status: 'active', // Revive if withered
      };

      if (canGrow) {
        const newWaterCount = idea.water_count + 1;
        updatePayload.water_count = newWaterCount;
        updatePayload.growth_stage = calculateGrowthStage(newWaterCount);
      }

      // AI expansion: expand 1-2 sections on each watering
      if (canExpandIdea(idea)) {
        try {
          const expansion = await expandIdeaOnWater(idea);
          Object.assign(updatePayload, expansion);
        } catch (aiError) {
          console.error('AI expansion failed:', aiError);
          // Continue without expansion
        }
      }

      // Update the idea
      const { data: updatedIdea, error: ideaError } = await supabase
        .from('ideas')
        .update(updatePayload)
        .eq('id', ideaId)
        .select()
        .single();

      if (ideaError) throw ideaError;

      // Update local state
      const updatedData = updatedIdea as unknown as Idea;
      setIdeas(prev => prev.map(i => i.id === updatedData.id ? updatedData : i));

      if (canGrow) {
        toast({
          title: 'Idea watered',
          description: `Your idea is now a ${updatedData.growth_stage}!`,
        });
      } else {
        toast({
          title: 'Reflection recorded',
          description: 'Growth needs time.',
        });
      }

      return true;
    } catch (error) {
      toast({
        title: 'Failed to water idea',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
      return false;
    }
  };

  const updateIdea = async (ideaId: string, updates: Partial<Idea>): Promise<boolean> => {
    if (!user) return false;

    try {
      const { data, error } = await supabase
        .from('ideas')
        .update(updates)
        .eq('id', ideaId)
        .select()
        .single();

      if (error) throw error;

      const updatedData = data as unknown as Idea;
      setIdeas(prev => prev.map(i => i.id === updatedData.id ? updatedData : i));
      return true;
    } catch (error) {
      toast({
        title: 'Failed to update idea',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
      return false;
    }
  };

  const archiveIdea = async (ideaId: string): Promise<boolean> => {
    return updateIdea(ideaId, { status: 'archived' });
  };

  const deleteIdea = async (ideaId: string): Promise<boolean> => {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from('ideas')
        .delete()
        .eq('id', ideaId);

      if (error) throw error;

      setIdeas(prev => prev.filter(i => i.id !== ideaId));
      return true;
    } catch (error) {
      toast({
        title: 'Failed to delete idea',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
      return false;
    }
  };

  const setFocus = async (ideaId: string): Promise<boolean> => {
    if (!user) return false;

    try {
      // The database trigger will automatically unset is_focus on other ideas
      const { data, error } = await supabase
        .from('ideas')
        .update({ is_focus: true })
        .eq('id', ideaId)
        .select()
        .single();

      if (error) throw error;

      // Update local state - set this idea as focus, unset others
      const updatedData = data as unknown as Idea;
      setIdeas(prev => prev.map(i => ({
        ...i,
        is_focus: i.id === ideaId
      })));

      toast({
        title: 'Focus set',
        description: 'This idea is now your focus.',
      });

      return true;
    } catch (error) {
      toast({
        title: 'Failed to set focus',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
      return false;
    }
  };

  const clearFocus = async (ideaId: string): Promise<boolean> => {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from('ideas')
        .update({ is_focus: false })
        .eq('id', ideaId);

      if (error) throw error;

      setIdeas(prev => prev.map(i => ({
        ...i,
        is_focus: i.id === ideaId ? false : i.is_focus
      })));

      return true;
    } catch (error) {
      toast({
        title: 'Failed to clear focus',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
      return false;
    }
  };

  return {
    ideas,
    loading,
    plantSeed,
    waterIdea,
    updateIdea,
    archiveIdea,
    deleteIdea,
    setFocus,
    clearFocus,
    refetch: fetchIdeas,
  };
}

function calculateGrowthStage(waterCount: number): GrowthStage {
  if (waterCount === 0) return 'seed';
  if (waterCount === 1) return 'sprout';
  if (waterCount <= 3) return 'plant';
  if (waterCount <= 6) return 'growing';
  return 'tree';
}
